import { Controller } from "@hotwired/stimulus";

const DEBOUNCE_MS = 300;

// Drives the invite-code field on the workspace registration form: validates
// the code against /register-workspace/validate_code as the user types, and
// (in code-only mode) reveals the workspace name/slug fields once a valid
// code is found. Those fields are `hidden` until revealed, so `required` is
// kept in sync with visibility here — a hidden-but-required field makes the
// browser's native validation UI throw "not focusable" and silently block
// submission.
export default class extends Controller {
  static targets = ["field", "status", "details", "name", "slug", "submit"];
  static values = {
    validateUrl: String,
    checking: String,
    validMsg: String,
    reasons: Object,
  };

  connect() {
    this._timer = null;
    this._requestId = 0;
    this._slugEdited = false;
    this._syncRequired(false);
  }

  disconnect() {
    clearTimeout(this._timer);
  }

  validate() {
    clearTimeout(this._timer);
    const token = this.fieldTarget.value.trim();

    if (token === "") {
      this._reset();
      return;
    }

    this._showStatus(this.checkingValue, null);
    this._timer = setTimeout(() => this._check(token), DEBOUNCE_MS);
  }

  slugify() {
    if (this._slugEdited || !this.hasSlugTarget) return;
    this.slugTarget.value = this.nameTarget.value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  slugEdited() {
    this._slugEdited = true;
  }

  async _check(token) {
    const requestId = ++this._requestId;
    try {
      const url = `${this.validateUrlValue}?token=${encodeURIComponent(token)}`;
      const resp = await fetch(url, { headers: { Accept: "application/json" } });
      if (requestId !== this._requestId) return;
      const body = await resp.json();
      if (requestId !== this._requestId) return;

      if (body.valid) {
        this._showStatus(this.validMsgValue.replace("%{count}", body.remaining), true);
        this._reveal(true);
      } else {
        this._showStatus(this.reasonsValue[body.reason] || this.reasonsValue.default, false);
        this._reveal(false);
      }
    } catch {
      if (requestId === this._requestId) this._reset();
    }
  }

  _reveal(valid) {
    if (this.hasDetailsTarget) this.detailsTarget.classList.toggle("hidden", !valid);
    this._syncRequired(valid);
    if (this.hasSubmitTarget) this.submitTarget.disabled = !valid;
  }

  _syncRequired(revealed) {
    const required = !this.hasDetailsTarget || revealed;
    if (this.hasNameTarget) this.nameTarget.required = required;
    if (this.hasSlugTarget) this.slugTarget.required = required;
  }

  _showStatus(text, valid) {
    this.statusTarget.textContent = text;
    this.statusTarget.classList.remove("hidden");
    this.statusTarget.classList.toggle("text-success", valid === true);
    this.statusTarget.classList.toggle("text-error", valid === false);
    this.fieldTarget.classList.toggle("input-error", valid === false);
    this.fieldTarget.classList.toggle("input-success", valid === true);
  }

  _reset() {
    this.statusTarget.textContent = "";
    this.statusTarget.classList.add("hidden");
    this.fieldTarget.classList.remove("input-error", "input-success");
    this._reveal(false);
  }
}
